import { arrayMove } from '@dnd-kit/sortable';

import { WikiPage } from './types';

export interface FlatPage {
  page: WikiPage;
  depth: number;
}

/** Flatten pages into visual (depth-first) order by parent_id + position, honoring collapsed ids. */
export function buildFlat(pages: WikiPage[], collapsed: Set<string>): FlatPage[] {
  const byParent = new Map<string, WikiPage[]>();
  const ids = new Set(pages.map((p) => p.id));
  for (const p of pages) {
    const key = p.parent_id && ids.has(p.parent_id) ? p.parent_id : '__root__';
    const arr = byParent.get(key) ?? [];
    arr.push(p);
    byParent.set(key, arr);
  }
  for (const arr of byParent.values()) arr.sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));
  const out: FlatPage[] = [];
  const walk = (parentKey: string, depth: number) => {
    for (const p of byParent.get(parentKey) ?? []) {
      out.push({ page: p, depth });
      const kids = byParent.get(p.id) ?? [];
      if (kids.length && !collapsed.has(p.id)) walk(p.id, depth + 1);
    }
  };
  walk('__root__', 0);
  return out;
}

export function childCount(pages: WikiPage[], id: string): number {
  return pages.reduce((n, p) => (p.parent_id === id ? n + 1 : n), 0);
}

/** ids of a page and all its descendants (for cycle-safe drag + the delete-subtree confirm). */
export function subtreeIds(pages: WikiPage[], id: string): Set<string> {
  const byParent = new Map<string, string[]>();
  for (const p of pages) if (p.parent_id) (byParent.get(p.parent_id) ?? byParent.set(p.parent_id, []).get(p.parent_id)!).push(p.id);
  const out = new Set<string>([id]);
  const walk = (x: string) => (byParent.get(x) ?? []).forEach((k) => (out.has(k) ? null : (out.add(k), walk(k))));
  walk(id);
  return out;
}

export interface Projection {
  depth: number;
  parentId: string | null;
}

/** Where a dragged page would land (depth + new parent), from the flattened list (with the active
 *  page's subtree removed), the over id and the horizontal drag offset. Adapted from the dnd-kit
 *  sortable-tree example (see pages/board/tree.ts). */
export function getProjection(flat: FlatPage[], activeId: string, overId: string, dragOffsetX: number, indentWidth: number): Projection {
  const overIndex = flat.findIndex((f) => f.page.id === overId);
  const activeIndex = flat.findIndex((f) => f.page.id === activeId);
  if (overIndex === -1 || activeIndex === -1) return { depth: 0, parentId: null };

  const moved = arrayMove(flat, activeIndex, overIndex);
  const prev = moved[overIndex - 1];
  const next = moved[overIndex + 1];

  const dragDepth = Math.round(dragOffsetX / indentWidth);
  const projectedDepth = flat[activeIndex].depth + dragDepth;
  const maxDepth = prev ? prev.depth + 1 : 0;
  const minDepth = next ? next.depth : 0;
  const depth = Math.max(minDepth, Math.min(projectedDepth, maxDepth));

  let parentId: string | null = null;
  if (depth !== 0 && prev) {
    if (depth === prev.depth) parentId = prev.page.parent_id ?? null;
    else if (depth > prev.depth) parentId = prev.page.id;
    else {
      const ancestor = moved
        .slice(0, overIndex)
        .reverse()
        .find((f) => f.depth === depth);
      parentId = ancestor ? ancestor.page.parent_id ?? null : null;
    }
  }
  return { depth, parentId };
}

/** The fractional position that places a page between its new previous and next SIBLINGS (same
 *  parent) in the flattened order. The dragged page (activeId) is skipped in both sibling scans — else
 *  it counts as its own boundary and an upward reorder / move-to-top silently no-ops. Mirrors the
 *  bisect-between-neighbours reorder used elsewhere. */
export function positionForDrop(flat: FlatPage[], overIndex: number, parentId: string | null, activeId: string): number {
  const siblingsBefore: WikiPage[] = [];
  const siblingsAfter: WikiPage[] = [];
  for (let i = 0; i <= overIndex && i < flat.length; i++)
    if ((flat[i].page.parent_id ?? null) === parentId && flat[i].page.id !== activeId) siblingsBefore.push(flat[i].page);
  for (let i = overIndex + 1; i < flat.length; i++)
    if ((flat[i].page.parent_id ?? null) === parentId && flat[i].page.id !== activeId) siblingsAfter.push(flat[i].page);
  const prev = siblingsBefore[siblingsBefore.length - 1];
  const next = siblingsAfter[0];
  if (prev && next) return (prev.position + next.position) / 2;
  if (prev) return prev.position + 1;
  if (next) return next.position - 1;
  return Date.now();
}
